import type { EstateModule } from "@/shared/constants/estateModules";
import type { FlowScreen, FlowScreenConfig, FlowAnswers } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

import { benefitsEligibilityTasks } from "./benefits-eligibility/tasks";
import { debtsLoansTasks } from "./debts-loans/tasks";
import { expensesTasks } from "./expenses/tasks";
import { financialAccountsTasks } from "./financial-accounts/tasks";
import { personalPropertyTasks } from "./property-valuables/tasks";
import { otherAssetsTasks } from "./other-assets/tasks";
import { realEstateTasks } from "./real-estate/tasks";

export interface ScreenFactoryContext {
  assets: EstateItem[];
  debts: EstateItem[];
  documentTypeLabels: Record<number, string>;
  fullName?: string;
}

export type ScreenFactory = (
  firstName: string,
  context?: ScreenFactoryContext,
) => FlowScreenConfig[];

const registry: Record<string, ScreenFactory> = {
  ...benefitsEligibilityTasks,
  ...debtsLoansTasks,
  ...expensesTasks,
  ...financialAccountsTasks,
  ...personalPropertyTasks,
  ...otherAssetsTasks,
  ...realEstateTasks,
};

export interface TaskDescriptor {
  taskId: string;
  screens: FlowScreen[];
}

export interface ModuleDescriptor {
  tasks: TaskDescriptor[];
  allScreens: FlowScreen[];
  screenTaskId: Record<string, string>;
}

function normalizeScreen(config: FlowScreenConfig): FlowScreen {
  if (!config.question) return config as FlowScreen;
  return {
    ...config,
    question: { ...config.question, id: config.id },
  } as FlowScreen;
}

export function getScreensForModule(
  module: EstateModule,
  firstName: string,
  context?: ScreenFactoryContext,
): ModuleDescriptor {
  const tasks: TaskDescriptor[] = [];
  const allScreens: FlowScreen[] = [];
  const screenTaskId: Record<string, string> = {};

  for (const task of module.tasks) {
    const factory = registry[task.taskId];
    if (!factory) continue;

    const rawScreens = factory(firstName, context);
    if (rawScreens.length === 0) continue;

    const taskScreens = rawScreens.map(normalizeScreen);

    tasks.push({
      taskId: task.taskId,
      screens: taskScreens,
    });

    for (const screen of taskScreens) {
      allScreens.push(screen);
      screenTaskId[screen.id] = task.taskId;
    }
  }

  return { tasks, allScreens, screenTaskId };
}

export function getResumeStep(
  screens: FlowScreen[],
  answers: FlowAnswers,
  screenTaskId: Record<string, string>,
  completedTaskIds: Set<string>,
): number {
  if (Object.keys(answers).length === 0 && completedTaskIds.size === 0) return 0;

  const screenIds = new Set(screens.map((s) => s.id));
  const hasScreenAnswers = Object.keys(answers).some((key) => screenIds.has(key) && !!answers[key]);
  if (!hasScreenAnswers && completedTaskIds.size === 0) return 0;

  const visibleScreens = screens.filter((screen) => {
    if (!screen.shouldDisplay) return true;
    return screen.shouldDisplay({ answers, currentStep: 0 });
  });

  const firstUnanswered = visibleScreens.findIndex((screen) => {
    const taskId = screenTaskId[screen.id];
    if (taskId && completedTaskIds.has(taskId)) return false;
    if (screen.question) return !answers[screen.question.id];
    if (screen.customComponent) return !answers[screen.id];
    const screenIndex = screens.indexOf(screen);
    const hasLaterAnswer = screens.slice(screenIndex + 1).some((s) => !!answers[s.id]);
    return !hasLaterAnswer;
  });
  if (firstUnanswered !== -1) return firstUnanswered;

  let reviewIndex = -1;
  for (let i = visibleScreens.length - 1; i >= 0; i--) {
    if (visibleScreens[i].id.includes("review")) {
      reviewIndex = i;
      break;
    }
  }
  return reviewIndex !== -1 ? reviewIndex : visibleScreens.length - 1;
}
